import { useMemo } from "react";
import { useGetOAuthProviders } from "@/app/oauth/api/hooks";
import { useGetSecrets } from "@/app/secrets/api/hooks";
import type { Tool } from "@/app/tools/types";

/**
 * Minimal tool shape required for requirements analysis.
 * This allows the hook to work with both full Tool objects and partial data.
 */
export type RequirementToolInput = {
  name: string;
  fully_qualified_name: string;
  requirements?: {
    secrets?: Array<{ key: string; met?: boolean }>;
    authorization?: {
      provider_type?: string;
      id?: string;
      provider_id?: string;
      status?: string;
      token_status?: string;
    } | null;
  };
};

/**
 * Result of analyzing a single tool's requirements
 */
export type SingleToolRequirementsAnalysis = {
  hasRequirements: boolean;
  hasSecrets: boolean;
  hasOAuth: boolean;
  allMet: boolean;
  secretsMet: boolean;
  oauthConfigured: boolean;
  oauthAuthorized: boolean;
  oauthMet: boolean;
  needsAction: boolean;
  badge: { className: string; label: string };
};

/**
 * A missing requirement with metadata
 */
type MissingRequirement = {
  type: "secret" | "oauth";
  key: string;
  displayName: string;
  toolNames: string[];
  isDisabled?: boolean;
};

/**
 * Result of batch requirements analysis across multiple tools
 */
export type BatchRequirementsAnalysis = {
  missingRequirements: MissingRequirement[];
  missingSecretsMap: Record<string, RequirementToolInput[]>;
  missingOAuthMap: Record<
    string,
    { tools: RequirementToolInput[]; displayName: string; isDisabled: boolean }
  >;
  hasMissingRequirements: boolean;
};

type OAuthProvider = {
  id?: string;
  provider_id?: string;
  status?: string;
};

/**
 * Check if a tool has any missing secret requirements
 */
function checkToolMissingSecrets(
  tool: RequirementToolInput,
  configuredSecretKeys: Set<string>
): string[] {
  const missingKeys: string[] = [];
  const secrets = tool.requirements?.secrets;

  if (!secrets || secrets.length === 0) {
    return missingKeys;
  }

  for (const secretRequirement of secrets) {
    const secretKey = secretRequirement.key.toLowerCase();
    if (!configuredSecretKeys.has(secretKey)) {
      missingKeys.push(secretKey);
    }
  }

  return missingKeys;
}

/**
 * Check if a tool has missing/disabled OAuth requirements
 */
function checkToolMissingOAuth(
  tool: RequirementToolInput,
  oauthProviderMap: Map<string, { status: string }>
): { key: string; displayName: string; isDisabled: boolean } | null {
  const auth = tool.requirements?.authorization;
  if (!auth || auth.provider_type !== "oauth2") {
    return null;
  }

  const authIdentifier = auth.id ?? auth.provider_id;
  if (!authIdentifier) {
    return null;
  }

  const authIdentifierLower = authIdentifier.toLowerCase();
  const existingProvider = oauthProviderMap.get(authIdentifierLower);

  const displayName =
    authIdentifier.charAt(0).toUpperCase() + authIdentifier.slice(1);

  if (!existingProvider) {
    return { key: authIdentifierLower, displayName, isDisabled: false };
  }

  if (existingProvider.status === "inactive") {
    return { key: authIdentifierLower, displayName, isDisabled: true };
  }

  return null;
}

/**
 * Build a map of missing secrets to tools
 */
function buildMissingSecretsMap(
  tools: RequirementToolInput[],
  configuredSecretKeys: Set<string>
): Record<string, RequirementToolInput[]> {
  const result: Record<string, RequirementToolInput[]> = {};

  for (const tool of tools) {
    const missingKeys = checkToolMissingSecrets(tool, configuredSecretKeys);
    for (const key of missingKeys) {
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(tool);
    }
  }

  return result;
}

/**
 * Build a map of missing/disabled OAuth providers to tools
 */
function buildMissingOAuthMap(
  tools: RequirementToolInput[],
  oauthProviderMap: Map<string, { status: string }>
): Record<
  string,
  { tools: RequirementToolInput[]; displayName: string; isDisabled: boolean }
> {
  const result: Record<
    string,
    { tools: RequirementToolInput[]; displayName: string; isDisabled: boolean }
  > = {};

  for (const tool of tools) {
    const missingOAuth = checkToolMissingOAuth(tool, oauthProviderMap);
    if (missingOAuth) {
      const { key, displayName, isDisabled } = missingOAuth;
      if (!result[key]) {
        result[key] = { tools: [], displayName, isDisabled };
      }
      result[key].tools.push(tool);
    }
  }

  return result;
}

/**
 * Authorization input for checking if OAuth is needed
 */
export type AuthorizationInput = {
  provider_type?: string;
  token_status?: string;
} | null;

/**
 * Check if OAuth authorization is needed for a tool.
 * Returns true if the tool requires OAuth2 and the user hasn't authorized yet.
 */
export function checkNeedsOAuthAuthorization(
  authorization: AuthorizationInput | undefined,
  authStatus?: "idle" | "pending" | "completed" | "error"
): boolean {
  const hasOAuth = authorization?.provider_type === "oauth2";
  const isOAuthAuthorized =
    authorization?.token_status === "completed" || authStatus === "completed";

  return hasOAuth && !isOAuthAuthorized;
}

const BADGE_STYLES = {
  allSet: {
    className:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
    label: "All set",
  },
  actionNeeded: {
    className:
      "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
    label: "Action needed",
  },
  enableProvider: {
    className:
      "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
    label: "Enable provider",
  },
  connectAccount: {
    className:
      "bg-neutral-100 text-neutral-700 dark:bg-neutral-800/50 dark:text-neutral-300",
    label: "Connect account",
  },
} as const;

/**
 * Analyze requirements for a single tool using API-provided met status.
 * This is useful when the tool already has `met` fields populated by the API.
 */
export function analyzeToolRequirements(
  tool: Tool,
  authStatus?: "idle" | "pending" | "completed" | "error"
): SingleToolRequirementsAnalysis {
  const secrets = tool.requirements?.secrets;
  const auth = tool.requirements?.authorization;

  const hasSecrets = (secrets?.length ?? 0) > 0;
  const hasOAuth = auth?.provider_type === "oauth2";
  const hasRequirements = hasSecrets || hasOAuth;

  const secretsMet =
    !hasSecrets || secrets?.every((s: { met?: boolean }) => s.met) === true;
  const oauthConfigured = auth?.status === "active";
  const oauthAuthorized =
    auth?.token_status === "completed" || authStatus === "completed";
  const oauthMet = !hasOAuth || oauthAuthorized;
  const allMet = secretsMet && oauthMet;

  const needsAction = !secretsMet;
  const onlyOAuthMissing = secretsMet && hasOAuth && !oauthMet;
  const providerNotConfigured = hasOAuth && !oauthConfigured;

  let badge: (typeof BADGE_STYLES)[keyof typeof BADGE_STYLES] =
    BADGE_STYLES.actionNeeded;
  if (allMet) {
    badge = BADGE_STYLES.allSet;
  } else if (onlyOAuthMissing) {
    badge = providerNotConfigured
      ? BADGE_STYLES.enableProvider
      : BADGE_STYLES.connectAccount;
  }

  return {
    hasRequirements,
    hasSecrets,
    hasOAuth,
    allMet,
    secretsMet,
    oauthConfigured,
    oauthAuthorized,
    oauthMet,
    needsAction,
    badge,
  };
}

/**
 * Hook for analyzing tool requirements status.
 *
 * Provides utilities for:
 * - Analyzing a single tool's requirements (using API-provided met status)
 * - Batch analysis of multiple tools (checking against configured secrets/providers)
 *
 * @example
 * // Single tool analysis (execute page)
 * const { analyzeToolRequirements } = useToolRequirementsStatus();
 * const status = analyzeToolRequirements(tool, authStatus);
 *
 * @example
 * // Batch analysis (tools page)
 * const { analyzeBatchRequirements } = useToolRequirementsStatus();
 * const { missingRequirements, hasMissingRequirements } = analyzeBatchRequirements(tools);
 */
export function useToolRequirementsStatus() {
  const { data: secrets = [] } = useGetSecrets();
  const { data: oauthProviders = [] } = useGetOAuthProviders();

  const configuredSecretKeys = useMemo(
    () => new Set(secrets.map((secret) => secret.key.toLowerCase())),
    [secrets]
  );

  const oauthProviderMap = useMemo(() => {
    const map = new Map<string, { status: string }>();
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    for (const provider of oauthProviders as OAuthProvider[]) {
      if (provider.id) {
        map.set(provider.id.toLowerCase(), { status: provider.status ?? "" });
      }
      if (provider.provider_id) {
        map.set(provider.provider_id.toLowerCase(), {
          status: provider.status ?? "",
        });
      }
    }
    return map;
  }, [oauthProviders]);

  /**
   * Analyze requirements for multiple tools and group by missing requirements.
   * Checks tools against actually configured secrets and OAuth providers.
   */
  const analyzeBatchRequirements = useMemo(
    () =>
      (tools: RequirementToolInput[]): BatchRequirementsAnalysis => {
        const missingSecretsMap = buildMissingSecretsMap(
          tools,
          configuredSecretKeys
        );
        const missingOAuthMap = buildMissingOAuthMap(tools, oauthProviderMap);

        const missingRequirements: MissingRequirement[] = [];

        for (const [key, toolsForSecret] of Object.entries(missingSecretsMap)) {
          missingRequirements.push({
            type: "secret",
            key,
            displayName: key,
            toolNames: toolsForSecret.map((t) => t.name),
          });
        }

        for (const [
          key,
          { tools: toolsForOAuth, displayName, isDisabled },
        ] of Object.entries(missingOAuthMap)) {
          missingRequirements.push({
            type: "oauth",
            key,
            displayName,
            toolNames: toolsForOAuth.map((t) => t.name),
            isDisabled,
          });
        }

        const hasMissingRequirements =
          Object.keys(missingSecretsMap).length > 0 ||
          Object.keys(missingOAuthMap).length > 0;

        return {
          missingRequirements,
          missingSecretsMap,
          missingOAuthMap,
          hasMissingRequirements,
        };
      },
    [configuredSecretKeys, oauthProviderMap]
  );

  /**
   * Check if a specific secret key is configured
   */
  const isSecretConfigured = useMemo(
    () =>
      (secretKey: string): boolean =>
        configuredSecretKeys.has(secretKey.toLowerCase()),
    [configuredSecretKeys]
  );

  /**
   * Check if an OAuth provider is configured and active
   */
  const isOAuthProviderActive = useMemo(
    () =>
      (providerId: string): boolean => {
        const provider = oauthProviderMap.get(providerId.toLowerCase());
        return provider?.status === "active";
      },
    [oauthProviderMap]
  );

  return {
    analyzeToolRequirements,
    analyzeBatchRequirements,
    isSecretConfigured,
    isOAuthProviderActive,
    configuredSecretKeys,
    oauthProviderMap,
  };
}
